import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { getCard, getContext, getItem, postOrder } from "./guestApi";
import { arm as armPager } from "./readyAlert";
import { cardUrl, clearGuest, loadGuest, saveGuest } from "./guestSession";
import { clearPlace, loadPlace, savePlace } from "./placeSession";
import useVenueManifest from "./useVenueManifest";
import FlowStrip from "./FlowStrip";
import OrderStatus from "./OrderStatus";
import ItemView from "./ItemView";
import OrderForm from "./OrderForm";
import ScanToOrder from "./ScanToOrder";
import TablePicker from "./TablePicker";
import "./scan-stay.css";

/**
 * "Scan & Stay" guest page (/v/:venueCode on THIS SPA's host, e.g.
 * app.popuppush.com) — ORDER-FIRST.
 *
 * Two ways in, one destination:
 *  • `?item={id}` — the per-item deep link printed on the screen QR. The guest
 *    lands directly on THAT dish. This is the path the whole flow is designed
 *    around: see the thing you want, say who you are once, send it.
 *  • bare `/v/{code}` or `?table={i}` — the table/venue QR, which can't know
 *    what caught someone's eye, so it shows the live items and the first tap
 *    lands on the same order screen.
 *
 * There is deliberately NO check-in step in front of any of this. Identity is
 * captured once, at order time, by the single phone field on the order screen —
 * and it mints nothing: every point is credited when a cashier marks the order
 * paid. The points/rewards card that used to live here is becoming the standalone
 * customer card (reached by typing a phone number), so a recognised guest sees
 * their balance inside the recognition card here rather than on a separate view.
 *
 * ⚠️ P5d — THE REMOTE-ORDER GATE. Ordering requires a `place_token`, minted only
 * when the guest arrives through a QR the venue physically put in the room:
 * `?v=1` (the printed venue QR), `?table={i}` (the table tent) or `?item={id}`
 * (the screen). A BARE `/v/{code}` gets no token and renders BROWSE-ONLY — the
 * menu and prices are still there, the Order action is not. That is not an
 * oversight in the bare link; it is the point. A bare link is what a bookmark,
 * a shared message and the saved customer card all look like, and one of those
 * was sending real tickets to a kitchen with nobody in the building.
 *
 * The gate is about PRESENCE, never identity — a recognised regular with 500
 * points is refused just the same, and the refusal tells them to scan, not who
 * they are. Enforcement is server-side (`PlaceToken`); everything here is the UI
 * telling the truth about what the server will accept.
 */

export default function ScanStay() {
  const { venueCode } = useParams();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const isAr = (i18n.resolvedLanguage || i18n.language || "en").startsWith("ar");

  // Table from a per-table QR. Sanitised to a short alphanumeric token so "7",
  // "A1" and "Patio3" all survive; only meaningful in table-service mode.
  const tableFromQr = (searchParams.get("table") || "").replace(/[^a-zA-Z0-9]/g, "").slice(0, 8);
  const itemFromQr = (searchParams.get("item") || "").trim();

  // P5d — which PHYSICAL QR this arrival came through, or null for a bare link.
  // `?v=1` is the marker carried by the printed venue QR and by the QR on the
  // venue's own screens; the other two markers are the params that were already
  // there. An `?item=` link asks for "venue" here only as a FALLBACK — the item
  // fetch below mints the accurate `item` entry, and this covers the case where
  // the deep link points at a dish that has since been deleted, so a guest who
  // genuinely scanned the screen isn't dropped into browse-only.
  const entry = tableFromQr
    ? "table"
    : searchParams.get("v") === "1" || itemFromQr
      ? "venue"
      : null;

  const [status, setStatus] = useState("loading"); // loading|error|menu|order|sent
  const [errorKind, setErrorKind] = useState("not_found");
  const [context, setContext] = useState(null);
  const [item, setItem] = useState(null);
  const [fromScreen, setFromScreen] = useState(false);

  const [qty, setQty] = useState(1);
  const [identity, setIdentity] = useState({ phone: "", phoneValid: false, name: "", known: false, pending: 0 });
  const [table, setTable] = useState(tableFromQr);
  const [touched, setTouched] = useState(false);
  const [sending, setSending] = useState(false);
  const [orderRes, setOrderRes] = useState(null);
  const [orderErr, setOrderErr] = useState("");
  // The remembered guest, re-validated against the server before it is trusted.
  const [guest, setGuest] = useState(null);
  // P5d — presence. Seeded from sessionStorage so a guest who scanned, ordered
  // and came back for a second round in the same tab is not asked to rescan.
  const [placeToken, setPlaceToken] = useState(() => loadPlace(venueCode));
  // Set when the SERVER refuses the token we hold. Distinct from simply not
  // having one: the guest was ordering a moment ago and needs to be told why
  // that stopped working, or the app just looks broken.
  const [gateExpired, setGateExpired] = useState(false);

  const serviceType = context?.venue?.service_type || "counter";
  const tableCount = context?.venue?.table_count ?? null;
  const currency = context?.venue?.currency_symbol || "";
  // A table QR pre-fills the number and the picker disappears; asking a guest to
  // confirm a table they already told us is the second scan we set out to avoid.
  const needsTable = serviceType === "table" && !tableFromQr;

  const localName = useCallback(
    (obj) => (isAr && obj?.name_ar ? obj.name_ar : obj?.name),
    [isAr]
  );

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const ctx = await getContext(venueCode, entry);
        if (!alive) return;
        setContext(ctx);
        // Only present when this arrival carried a physical-QR marker. A bare
        // load returns no key at all, and we must not clear an existing token
        // on one — a guest who scanned the table QR and then tapped through to
        // a bare link in the same tab is still sitting at that table.
        if (ctx?.place_token) {
          savePlace(venueCode, ctx.place_token);
          setPlaceToken(ctx.place_token);
          setGateExpired(false);
        }

        if (itemFromQr) {
          try {
            // The deep-link fetch is separate from context on purpose: context
            // only lists items inside their daypart window, and a QR
            // photographed at lunch is opened at 23:05.
            const res = await getItem(venueCode, itemFromQr);
            if (!alive) return;
            // The accurate `item` entry, replacing the `venue` fallback above.
            if (res?.place_token) {
              savePlace(venueCode, res.place_token);
              setPlaceToken(res.place_token);
              setGateExpired(false);
            }
            setItem(res.item);
            setFromScreen(true);
            setStatus("order");
            return;
          } catch (_) {
            // A dead item id shouldn't dead-end the guest — fall through to the
            // menu so they can still order something.
            if (!alive) return;
          }
        }
        setStatus("menu");
      } catch (e) {
        if (!alive) return;
        setErrorKind(e?.response?.status === 404 ? "not_found" : "network");
        setStatus("error");
      }
    })();
    return () => {
      alive = false;
    };
  }, [venueCode, itemFromQr, entry]);

  // Restore the cached session. The token is a CONVENIENCE cache, never the
  // source of truth: it is re-validated against the card endpoint, and a rotated
  // or revoked one 404s and is dropped. That ordering is the whole fix for the
  // old localStorage "known guest", which was authoritative and therefore wrong
  // on a borrowed phone.
  useEffect(() => {
    let ok = true;
    const cached = loadGuest(venueCode);
    if (!cached?.token) return undefined;
    (async () => {
      try {
        const card = await getCard(cached.token);
        if (!ok) return;
        setGuest({
          token: cached.token,
          customerId: card?.customer?.id || cached.customerId || null,
          name: card?.customer?.name || cached.name || "",
          balance: card?.points?.balance ?? 0,
          pending: card?.points?.pending ?? 0,
        });
      } catch (e) {
        if (!ok) return;
        if (e?.response?.status === 404) clearGuest(venueCode);
      }
    })();
    return () => { ok = false; };
  }, [venueCode]);

  const forgetGuest = useCallback(() => {
    clearGuest(venueCode);
    setGuest(null);
    setIdentity({ phone: "", phoneValid: false, name: "", known: false, pending: 0 });
  }, [venueCode]);

  const featuredItems = useMemo(
    () => context?.featured_items ?? (context?.featured_item ? [context.featured_item] : []),
    [context]
  );

  const chooseItem = (it) => {
    // Items from context are inside their window by definition; the deep-link
    // shape carries live/available, so normalise to it.
    setItem({ ...it, live: true, available: true });
    setFromScreen(false);
    setQty(1);
    setOrderErr("");
    setStatus("order");
  };

  useVenueManifest(context?.venue?.name, venueCode);

  const toggleLang = () => i18n.changeLanguage(isAr ? "en" : "ar");

  // P5d — the one question the whole gate reduces to. Note it is INDEPENDENT of
  // identity: `guest`, `identity` and the card token have no bearing on it.
  const canOrder = Boolean(placeToken) && !gateExpired;

  const nameMissing = !guest && !identity.known && identity.name.length < 2;
  const tableMissing = needsTable && !table.trim();
  const canSend =
    canOrder && (guest || identity.phoneValid) && !nameMissing && !tableMissing && !sending;

  const submit = async () => {
    // Arm the pager FIRST, synchronously, before any await. This tap is the only
    // user gesture the flow is guaranteed to get, and a browser will not start
    // audio that no human asked for — the refusal is silent, so an alert armed
    // any later would look fine right up until the moment it had to ring. It
    // also carries the notification-permission prompt, which Safari likewise
    // only allows from a gesture. Deliberately not awaited and never fatal: a
    // guest who denies everything still gets the visual ready state.
    armPager();

    setTouched(true);
    setOrderErr("");
    // Presence first, and silently: the button is already replaced by the scan
    // prompt in this state, so an error line under it would be a second voice
    // saying the same thing.
    if (!canOrder) return;
    if (!guest && !identity.phoneValid) return setOrderErr(t("scan_stay.err_phone"));
    if (nameMissing) return setOrderErr(t("scan_stay.err_name"));
    if (tableMissing) return setOrderErr(t("scan_stay.order_err_table"));

    setSending(true);
    try {
      const res = await postOrder({
        venue_code: venueCode,
        // P5d — proof of presence. The server re-checks the signature, the venue
        // and the expiry; this is the client's half of the same statement.
        place_token: placeToken,
        featured_item_id: item?.id,
        quantity: qty,
        // Always send the name: the server uses it to name the guest record even
        // when the ORDER row keeps it only for counter service.
        customer_name: identity.name || guest?.name || undefined,
        table_number: serviceType === "table" ? (tableFromQr || table).trim() : undefined,
        // A remembered guest orders by id — they never retyped a number, so
        // there is no phone to send. Newly-identified guests send the phone and
        // the server does recognise-or-capture.
        ...(guest?.customerId && !identity.phone
          ? { customer_id: guest.customerId }
          : { phone: identity.phone }),
      });
      if (res?.customer?.card_token) {
        saveGuest(venueCode, {
          token: res.customer.card_token,
          customerId: res.customer.id,
          name: res.customer.name,
        });
        setGuest((g) => ({
          ...(g || {}),
          token: res.customer.card_token,
          customerId: res.customer.id,
          name: res.customer.name,
        }));
      }
      setOrderRes(res);
      setStatus("sent");
    } catch (e) {
      const code = e?.response?.data?.error;
      // P5d — the server refused the token we were holding: aged out, or a
      // local copy that no longer verifies. Drop it and swap the CTA for the
      // scan prompt rather than showing a red failure line, because nothing
      // failed — the guest just has to prove they are still here.
      if (code === "place_token_expired" || code === "place_token_required") {
        clearPlace(venueCode);
        setPlaceToken(null);
        setGateExpired(true);
        return;
      }
      setOrderErr(code === "invalid_phone" ? t("scan_stay.err_phone") : t("scan_stay.order_failed"));
    } finally {
      setSending(false);
    }
  };

  const venueName = context?.venue?.name || "";
  const initial = (venueName || "•").trim().charAt(0).toUpperCase();

  const Header = (
    <div className="ss-top">
      <div className="ss-mark" aria-hidden="true">{initial}</div>
      <div>
        <div className="ss-venue-name">{venueName}</div>
        <div className="ss-venue-sub">{t("scan_stay.loyalty_club")}</div>
      </div>
      <button type="button" className="ss-lang" onClick={toggleLang} aria-label="Toggle language">
        {isAr ? "EN" : "عربي"}
      </button>
    </div>
  );

  if (status === "loading") {
    return (
      <div className="ss-root" dir={isAr ? "rtl" : "ltr"}>
        <div className="ss-center">
          <div className="ss-spin" />
          <div>{t("scan_stay.loading")}</div>
        </div>
      </div>
    );
  }

  if (status === "error") {
    const net = errorKind === "network";
    return (
      <div className="ss-root" dir={isAr ? "rtl" : "ltr"}>
        <div className="ss-center">
          <div style={{ fontSize: 40 }}>{net ? "📡" : "🔍"}</div>
          <div style={{ fontWeight: 700, color: "var(--ss-ink)" }}>
            {t(net ? "scan_stay.connection_error" : "scan_stay.venue_not_found")}
          </div>
          <div>{t(net ? "scan_stay.connection_error_sub" : "scan_stay.venue_not_found_sub")}</div>
          {net && (
            <button type="button" className="ss-cta" style={{ maxWidth: 220, marginTop: 6 }}
              onClick={() => window.location.reload()}>
              {t("scan_stay.retry")}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="ss-root" dir={isAr ? "rtl" : "ltr"}>
      <div className="ss-card">
        {Header}

        {status === "menu" && (
          <>
            <FlowStrip current="scan" />
            {featuredItems.length === 0 ? (
              <div className="ss-center" style={{ padding: "28px 8px" }}>
                <div style={{ fontSize: 34 }}>🍽️</div>
                <div>{t("scan_stay.order.nothing_live")}</div>
              </div>
            ) : (
              <>
                <div className="ss-divider">{t("scan_stay.featured_today")}</div>
                <div className="ss-featured-list">
                  {featuredItems.map((it) => (
                    <div
                      className={`ss-featured${canOrder ? " tappable" : ""}`}
                      key={it.id}
                      {...(canOrder
                        ? {
                            role: "button",
                            tabIndex: 0,
                            onClick: () => chooseItem(it),
                            onKeyDown: (e) => {
                              if (e.key === "Enter" || e.key === " ") { e.preventDefault(); chooseItem(it); }
                            },
                          }
                        : {})}
                    >
                      <div className="img">
                        {it.image_url && (
                          <img className="ss-food-photo" src={it.image_url} alt={localName(it)} loading="lazy" />
                        )}
                        <span className="badge">{t("scan_stay.featured_badge")}</span>
                      </div>
                      <div className="body">
                        <div className="fn">{localName(it)}</div>
                        <span className="price">
                          {currency} {it.discount_price ?? it.price}
                          {it.discount_price != null && <s>{currency} {it.price}</s>}
                        </span>
                        {/* Browse-only keeps the dish, the photo and the price —
                            a menu is worth reading from anywhere. It drops only
                            the thing that would send food to a kitchen. */}
                        {canOrder && (
                          <button type="button" className="ss-order-mini"
                            onClick={(e) => { e.stopPropagation(); chooseItem(it); }}>
                            {t("scan_stay.order_now")}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* One prompt under the list, not one per tile: the guest is being
                asked for a single action, and repeating it beside every dish
                would read as an error state rather than an instruction. */}
            {!canOrder && <ScanToOrder expired={gateExpired} />}
          </>
        )}

        {status === "order" && item && (
          <>
            <FlowStrip current="order" />

            <ItemView
              item={item}
              currency={currency}
              qty={qty}
              onQty={setQty}
              fromScreen={fromScreen}
            />

            <OrderForm
              venueCode={venueCode}
              onChange={setIdentity}
              invalidPhone={touched && !guest && !identity.phoneValid}
              invalidName={touched && nameMissing}
              knownGuest={guest}
              onForgetGuest={forgetGuest}
              cardHref={guest?.token ? cardUrl(guest.token) : null}
            />

            {needsTable && (
              <TablePicker
                tableCount={tableCount}
                value={table}
                onChange={setTable}
                invalid={touched && tableMissing}
              />
            )}

            {tableFromQr && serviceType === "table" && (
              <div className="ss-table-banner">🍽️ {t("scan_stay.at_table", { table: tableFromQr })}</div>
            )}

            {orderErr && <div className="ss-err">{orderErr}</div>}

            {/* P5d — the CTA and the gate are the SAME slot. A disabled "Send to
                kitchen" would leave the guest guessing which field was wrong;
                the prompt names the one thing that is actually missing. */}
            {canOrder ? (
              <>
                <button type="button" className="ss-cta" disabled={!canSend} onClick={submit}>
                  {sending ? t("scan_stay.order_sending") : t("scan_stay.order.send_to_kitchen")}
                </button>

                <p className="ss-paynote">{t("scan_stay.order.pay_note")}</p>
                {/* Say what the tap will do. A guest who is about to be asked for
                    notification permission should know why, and one who is going to
                    be buzzed later should know to expect it rather than assume the
                    phone misbehaved. */}
                <p className="ss-paynote pager">🔔 {t("scan_stay.order.pager_promise")}</p>
              </>
            ) : (
              <ScanToOrder expired={gateExpired} />
            )}
          </>
        )}

        {status === "sent" && orderRes?.order && (
          <>
            <FlowStrip current="kitchen" />
            {/* Live now, not a receipt: the ticket polls preparing → ready, so a
                guest can tell a busy kitchen from a lost order. "Order another"
                keeps the session, so they are never asked who they are twice. */}
            <OrderStatus
              orderId={orderRes.order.id}
              initialOrder={orderRes.order}
              guestName={identity.name || guest?.name || ""}
              cardHref={guest?.token ? cardUrl(guest.token) : null}
              onOrderAnother={() => {
                setOrderRes(null);
                setItem(null);
                setQty(1);
                setTouched(false);
                setStatus("menu");
              }}
            />
          </>
        )}
      </div>

      <div className="ss-foot">{t("scan_stay.powered_by")}</div>
    </div>
  );
}
